/**
 * Pre-export gap check (spec 19).
 *
 * Two ways in, one shape out:
 *  - the overview pages ask the server (`getArtifactAnnotations`) because the
 *    draft body lives across many segments they never load;
 *  - the section editor already holds its content, so it scans locally with
 *    `findAnnotations` and skips the round trip.
 *
 * Neither path decides anything about the export itself — the server strips
 * annotations regardless of what the author clicks. This is information.
 */

import type { AnnotationReport } from '@/lib/api'
import { findAnnotations, outermostAnnotations, stripAnnotations } from '@/lib/annotations'

export interface ExportGapSection {
  sectionKey: string
  title: string
  todos: string[]
  malformedCount: number
}

export interface MappedAnnotationReport {
  sections: ExportGapSection[]
  attachmentCount: number
  /**
   * The "Review gaps" jump target: the first affected section, in outline order,
   * that actually RESOLVED against the outline. Null when nothing resolved —
   * `__document__` and dropped-section keys are labelled for the reader but are
   * not routable, and navigating to `/sections/__document__` would 404.
   */
  firstSectionKey: string | null
}

/**
 * Open the dialog on gaps OR on a malformed token.
 *
 * An attachment-only draft exports silently: `[ATT: …]` is guidance the author
 * wrote for themselves, its removal costs the reader nothing, and stopping the
 * export for it would train people to click through the dialog.
 */
export function shouldConfirmExport(report: AnnotationReport): boolean {
  return report.totalTodos > 0 || report.hasMalformed
}

/**
 * Resolve the report's section keys against the outline the page already holds.
 *
 * The endpoint returns sections alphabetical by key and does NOT filter to the
 * outline (deliberately — under-reporting a gap before an export is worse than
 * over-reporting one). So this both re-sorts into document order and gives every
 * unresolvable key a readable label: `__document__`, and any section dropped
 * from the outline while its drafting session stayed active.
 *
 * A single document's groups carry their own `title` (the heading the gaps sit
 * under), which wins over both.
 */
export function mapAnnotationReport(
  report: AnnotationReport,
  outlineSections: ReadonlyArray<{ sectionKey: string; title: string }>,
  fallbackTitle: string,
): MappedAnnotationReport {
  const orderByKey = new Map<string, number>()
  const titleByKey = new Map<string, string>()
  outlineSections.forEach((section, index) => {
    orderByKey.set(section.sectionKey, index)
    titleByKey.set(section.sectionKey, section.title)
  })

  // Unresolved keys sort after every outline section, keeping their arrival
  // order among themselves. `arrival` is the tie-breaker rather than relying on
  // sort stability, and the sentinel is finite: `Infinity - Infinity` is NaN,
  // which would leave two unresolved sections in an arbitrary order.
  const unresolvedRank = outlineSections.length
  const ranked = report.sections
    .map((section, arrival) => ({
      sectionKey: section.sectionId,
      title: section.title ?? titleByKey.get(section.sectionId) ?? fallbackTitle,
      todos: section.todos,
      malformedCount: section.malformedCount ?? 0,
      rank: orderByKey.get(section.sectionId) ?? unresolvedRank,
      arrival,
    }))
    .sort((a, b) => a.rank - b.rank || a.arrival - b.arrival)

  const attachmentCount = report.sections.reduce(
    (total, section) => total + (section.attachmentCount ?? 0),
    0,
  )

  const sections: ExportGapSection[] = ranked.map(
    ({ sectionKey, title, todos, malformedCount }) => ({ sectionKey, title, todos, malformedCount }),
  )

  return {
    sections,
    attachmentCount,
    firstSectionKey: ranked.find((s) => s.rank < unresolvedRank)?.sectionKey ?? null,
  }
}

export interface LocalGapScan {
  todos: string[]
  attachmentCount: number
  malformedCount: number
}

/**
 * Editor-side scan of content already in hand — no network call.
 *
 * Counts RAW tokens in the content string. The per-heading gap counts in the
 * editor header (spec 19, Task 9) count only tokens the decoration plugin
 * managed to CHIP, which needs parsed heading boundaries and silently skips
 * Markdown-bodied segments. This number is the authoritative one for export and
 * may legitimately exceed the header's — they are measuring different things.
 */
export function scanContentForGaps(content: string): LocalGapScan {
  const annotations = outermostAnnotations(findAnnotations(content))
  const stripped = stripAnnotations(content)

  return {
    todos: annotations.filter((a) => a.type === 'todo').map((a) => a.text),
    attachmentCount: annotations.filter((a) => a.type !== 'todo').length,
    // Every well-formed token is gone by now, so a surviving `[TODO:` / `[ATT:`
    // prefix IS a malformed one — no fourth regex to keep in step with the
    // scanner. Mirrors the backend's countMalformedAnnotations.
    malformedCount: countOccurrences(stripped, '[TODO:') + countOccurrences(stripped, '[ATT:'),
  }
}

function countOccurrences(haystack: string, needle: string): number {
  let count = 0
  let index = haystack.indexOf(needle)
  while (index !== -1) {
    count += 1
    index = haystack.indexOf(needle, index + needle.length)
  }
  return count
}
